import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { v4 as UUID } from "uuid";

import {
  ASSIGN_WORK_ORDER_TO_FINANCE_INVOICE_MUTATION,
  ATTACH_FINANCE_INVOICE_PURCHASE_ORDER_MUTATION,
  DELETE_FINANCE_INVOICE_MUTATION,
  DOWNLOAD_FINANCE_INVOICE_PDF_MUTATION,
  DUPLICATE_FINANCE_INVOICE_MUTATION,
  GET_FINANCE_INVOICE_QUERY,
  LIST_EMAIL_THREADS_BY_FINANCE_INVOICE_QUERY,
  LIST_FINANCE_INVOICE_ACTIVITY_LOGS_QUERY,
  REMOVE_FINANCE_INVOICE_PURCHASE_ORDER_MUTATION,
  SEND_FINANCE_INVOICE_MUTATION,
  UPDATE_FINANCE_INVOICE_MUTATION,
} from "~/api";
import type { FinanceInvoice } from "~/types/finance";
import { errorsHandler } from "~/transformers";

const financeInvoiceActivityLogsRefetch = (invoiceId: string) => ({
  query: LIST_FINANCE_INVOICE_ACTIVITY_LOGS_QUERY,
  variables: {
    invoiceId,
    params: { limit: 20, skip: 0, sort: ["created_at:desc"] },
  },
});

const financeInvoiceDetailRefetches = (invoiceId: string) => [
  { query: GET_FINANCE_INVOICE_QUERY, variables: { id: invoiceId } },
  financeInvoiceActivityLogsRefetch(invoiceId),
];

const evictFinanceInvoiceActivityLogs = (cache: {
  evict: (args: object) => void;
  gc: () => void;
}) => {
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "listFinanceInvoiceActivityLogs",
  });
  cache.gc();
};

/**
 * Backend requires a `scope` alongside invoiceId/emailAddressId for
 * `listEmailThreadsByFinanceInvoice`. Quotes' equivalent query has no scope
 * param, so this is the finance-invoice module scope value.
 */
export const FINANCE_INVOICE_EMAIL_SCOPE = "FINANCE_INVOICE";

export const useGetFinanceInvoiceDetail = (id?: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getFinanceInvoice: FinanceInvoice | null;
  }>(GET_FINANCE_INVOICE_QUERY, {
    skip: !id,
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  return {
    invoice: data?.getFinanceInvoice ?? null,
    loading,
    error,
    refetch,
  };
};

export const useUpdateFinanceInvoice = () => {
  const [mutate, { loading }] = useMutation(UPDATE_FINANCE_INVOICE_MUTATION);

  return {
    updateFinanceInvoice: (
      id: string,
      input: Record<string, unknown>,
    ): Promise<FinanceInvoice> =>
      new Promise((resolve, reject) => {
        mutate({
          variables: { input: { _id: id, ...input } },
          refetchQueries: financeInvoiceDetailRefetches(id),
          update: (cache) => {
            evictFinanceInvoiceActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.updateFinanceInvoice))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useSendFinanceInvoice = () => {
  const [mutate, { loading }] = useMutation(SEND_FINANCE_INVOICE_MUTATION);
  return {
    sendFinanceInvoice: (input: Record<string, unknown>) =>
      new Promise<FinanceInvoice>((resolve, reject) => {
        mutate({
          variables: { input },
          refetchQueries: financeInvoiceDetailRefetches(
            input.invoiceId as string,
          ),
          update: (cache) => {
            evictFinanceInvoiceActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.sendFinanceInvoice))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useDownloadFinanceInvoicePdf = () => {
  const [mutate, { loading }] = useMutation(
    DOWNLOAD_FINANCE_INVOICE_PDF_MUTATION,
  );
  return {
    downloadFinanceInvoicePDF: async (invoiceId: string) => {
      if (!invoiceId) return { success: false as const };
      const uuid = UUID();
      sessionStorage.setItem(invoiceId, uuid);
      try {
        await mutate({ variables: { invoiceId, uuid } });
        return { success: true as const };
      } catch (err) {
        return { success: false as const, error: errorsHandler(err) };
      }
    },
    loading,
  };
};

export const useDuplicateFinanceInvoice = () => {
  const [mutate, { loading }] = useMutation(DUPLICATE_FINANCE_INVOICE_MUTATION);
  return {
    duplicateFinanceInvoice: (id: string) =>
      new Promise<{ _id: string; invoiceNumber?: string }>(
        (resolve, reject) => {
          mutate({ variables: { id } })
            .then(({ data }) => resolve(data?.duplicateFinanceInvoice))
            .catch((err) => reject(errorsHandler(err)));
        },
      ),
    loading,
  };
};

export const useDeleteFinanceInvoice = () => {
  const [mutate, { loading }] = useMutation(DELETE_FINANCE_INVOICE_MUTATION);
  return {
    deleteFinanceInvoice: (id: string) =>
      new Promise<void>((resolve, reject) => {
        mutate({ variables: { id } })
          .then(() => resolve())
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useAssignWorkOrderToFinanceInvoice = () => {
  const [mutate, { loading }] = useMutation(
    ASSIGN_WORK_ORDER_TO_FINANCE_INVOICE_MUTATION,
  );
  return {
    assignWorkOrderToFinanceInvoice: (invoiceId: string, workOrderId: string) =>
      new Promise<FinanceInvoice>((resolve, reject) => {
        mutate({
          variables: { invoiceId, workOrderId },
          refetchQueries: financeInvoiceDetailRefetches(invoiceId),
          awaitRefetchQueries: true,
          update: (cache) => {
            evictFinanceInvoiceActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.assignWorkOrderToFinanceInvoice))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useListFinanceInvoiceActivityLogs = (
  invoiceId?: string,
  skip = false,
) => {
  const { data, loading, fetchMore, refetch } = useQuery(
    LIST_FINANCE_INVOICE_ACTIVITY_LOGS_QUERY,
    {
      skip: !invoiceId || skip,
      variables: {
        invoiceId,
        params: { limit: 20, skip: 0, sort: ["created_at:desc"] },
      },
      fetchPolicy: "cache-and-network",
    },
  );
  const result = data?.listFinanceInvoiceActivityLogs;
  return {
    activityLogs: result?.logs ?? [],
    totalCount: result?.totalCount ?? 0,
    loading,
    fetchMore,
    refetch,
  };
};

export const useListEmailThreadsByFinanceInvoiceLazy = () => {
  const [listThreads, { loading, refetch }] = useLazyQuery(
    LIST_EMAIL_THREADS_BY_FINANCE_INVOICE_QUERY,
    { fetchPolicy: "network-only" },
  );

  const fetchThreads = useCallback(
    async (invoiceId: string, emailAddressId: string) => {
      const result = await listThreads({
        variables: {
          scope: FINANCE_INVOICE_EMAIL_SCOPE,
          invoiceId,
          emailAddressId,
        },
      });
      return (result.data?.listEmailThreadsByFinanceInvoice ?? []) as unknown[];
    },
    [listThreads],
  );

  return { fetchThreads, loading, refetch };
};

export const useAttachFinanceInvoicePurchaseOrder = () => {
  const [mutate, { loading }] = useMutation(
    ATTACH_FINANCE_INVOICE_PURCHASE_ORDER_MUTATION,
  );
  return {
    attachFinanceInvoicePurchaseOrder: (
      invoiceId: string,
      purchaseOrder: {
        name?: string;
        url: string;
        type?: string;
        size?: string;
      },
    ) =>
      new Promise<FinanceInvoice>((resolve, reject) => {
        mutate({
          variables: { invoiceId, purchaseOrder },
          refetchQueries: financeInvoiceDetailRefetches(invoiceId),
          update: (cache) => {
            evictFinanceInvoiceActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.attachFinanceInvoicePurchaseOrder))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useRemoveFinanceInvoicePurchaseOrder = () => {
  const [mutate, { loading }] = useMutation(
    REMOVE_FINANCE_INVOICE_PURCHASE_ORDER_MUTATION,
  );
  return {
    removeFinanceInvoicePurchaseOrder: (invoiceId: string, url?: string) =>
      new Promise<FinanceInvoice>((resolve, reject) => {
        mutate({
          variables: { invoiceId, url },
          refetchQueries: financeInvoiceDetailRefetches(invoiceId),
          update: (cache) => {
            evictFinanceInvoiceActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.removeFinanceInvoicePurchaseOrder))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};
